import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { InputField } from "../../components/ui/InputField";
import Button from "../../components/ui/Button";
import AuthLayout from "../../components/layouts/AuthLayout";
import { AuthAPI } from "../../services/api";

export function SignUpPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Form data
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [agreePrivacyPolicy, setAgreePrivacyPolicy] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(false);

  const handleStep1Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || !confirmPassword) return;

    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    if (!agreePrivacyPolicy) {
      setError("Please agree to the Privacy Policy to continue");
      return;
    }

    if (!agreeMarketing) {
      setError("Please agree to receive marketing updates to continue");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const data = await AuthAPI.register({
        email,
        password,
        password_confirmation: confirmPassword,
        full_name: "", // Temporary - will be updated in profile setup
        phone: "", // Temporary placeholder
      });

      if (data.success) {
        // Store the verification token from the response
        if (data.verification_token) {
          setVerificationToken(data.verification_token);
        }
        setStep(2); // Move to success step
      } else {
        setError(data.message || "Registration failed");
      }
    } catch (err: any) {
      setError(err.message || "Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSimulateEmailClick = () => {
    // Simulate clicking email link - navigate to verification page with token
    const tokenToUse = verificationToken || "fallback-test-token-123";
    navigate(`/auth/verify-email?token=${tokenToUse}`);
  };

  const handleSignInRedirect = () => {
    navigate("/auth/signin");
  };

  const renderStep1 = () => (
    <>
      <form
        onSubmit={handleStep1Submit}
        className="flex relative flex-col gap-1.5 items-start z-[2] w-[80%]"
      >
        <div className="text-sm text-[#403634]">
          Please enter your email in the field below to receive a verification
          link.
        </div>
        <InputField
          label="Your Email"
          placeholder="Email@example.com"
          type="email"
          value={email}
          onChange={setEmail}
        />

        <InputField
          label="Type new password here"
          placeholder="Password"
          type="password"
          value={password}
          onChange={setPassword}
        />

        <InputField
          label="Confirm new password"
          placeholder="Re-enter Password"
          type="password"
          value={confirmPassword}
          onChange={setConfirmPassword}
        />

        {/* Privacy Policy and Marketing Consent Checkboxes */}
        <div className="flex flex-col gap-3 mt-4 w-full text-left">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={agreePrivacyPolicy}
              onChange={(e) => setAgreePrivacyPolicy(e.target.checked)}
              className="mt-1 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
            />
            <span className="text-sm text-[#403634]">
              I agree to AIM Academy's{" "}
              <a
                href="https://drive.google.com/file/d/1gIxetw5XdYs-DTIKSsBoEJge_PRxQmRC/view?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Privacy Policy
              </a>
              .
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={agreeMarketing}
              onChange={(e) => setAgreeMarketing(e.target.checked)}
              className="mt-1 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
            />
            <span className="text-sm text-[#403634]">
              I agree to receive updates about courses, events, and promotions
              from AIM Academy and its partners
            </span>
          </label>
        </div>
      </form>

      {error && (
        <div className="relative self-stretch text-sm text-red-600 text-center z-[2]">
          {error}
        </div>
      )}

      <Button
        onClick={handleStep1Submit}
        disabled={
          loading ||
          !email ||
          !password ||
          !confirmPassword ||
          !agreePrivacyPolicy ||
          !agreeMarketing
        }
        size="sm"
        variant="primary"
        className="z-[2]"
        loading={loading}
      >
        Sign Up
      </Button>

      <div className="flex relative gap-2.5 justify-center items-start self-stretch z-[2]">
        <span className="text-sm text-stone-500 max-sm:text-xs">
          Already have an account? Please
        </span>
        <button
          type="button"
          onClick={handleSignInRedirect}
          className="text-sm font-semibold cursor-pointer text-stone-900 max-sm:text-xs hover:text-stone-700 transition-colors"
        >
          Sign in
        </button>
      </div>
    </>
  );

  const renderStep2 = () => (
    <>
      <div className="relative text-center z-[2] space-y-4">
        <div className="text-lg font-semibold text-[#403634]">
          PLEASE CHECK YOUR EMAIL!
        </div>
        <div className="text-sm text-[#403634]">
          A verify link has been sent to your mail box, please check and click
          into the link inside to verify your email.
        </div>
      </div>
    </>
  );

  const getTitle = () => {
    switch (step) {
      case 1:
        return "";
      case 2:
        return "";
      case 3:
        return "WELCOME!";
      default:
        return "SIGN UP";
    }
  };

  const renderContent = () => {
    switch (step) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      default:
        return renderStep1();
    }
  };

  return <AuthLayout title={getTitle()}>{renderContent()}</AuthLayout>;
}

export default SignUpPage;
